import { Component, computed, inject, signal, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatDialogRef, MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

import { UsersService } from '../../../service/entity-services/users.service';
import { PermissionsClientService } from '../../../service/permissions/permissions-client.service';
import { normalizeEmail } from '../../../shared/utils/normalize-email';
import { environment } from '../../../../environments/environment';

/**
 * Admin-only dialog to pre-add a user (email + first/last name + initial activation)
 * before they've ever logged in. Saves via PermissionsClientService.createUser, which
 * hits the new hc-users create endpoint. Duplicates are blocked: an email already in
 * the roster is rejected inline, and a backend 409 is surfaced with the same message
 * (never overwrites an existing record). Assigning role/permissions happens separately
 * on the Permissions screen. Returns `true` on a successful create.
 */
@Component({
  selector: 'app-add-user-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatSlideToggleModule,
    MatProgressSpinnerModule,
  ],
  template: `
    <h2 mat-dialog-title>
      <mat-icon>person_add</mat-icon>
      Add User
    </h2>

    <mat-dialog-content>
      <form [formGroup]="form">
        <mat-form-field appearance="outline" class="full-width">
          <mat-label>Email</mat-label>
          <input matInput type="email" formControlName="email" [placeholder]="emailExample" (input)="errorMessage.set('')">
          <mat-icon matSuffix>email</mat-icon>
          @if (form.get('email')?.hasError('required')) {
            <mat-error>Email is required</mat-error>
          } @else if (form.get('email')?.hasError('email')) {
            <mat-error>Enter a valid email address</mat-error>
          } @else if (isDuplicate()) {
            <mat-error>This user already exists — edit them from the list or Permissions instead.</mat-error>
          }
          <mat-hint>{{ sso ? 'Must match the email the user signs in with via SSO.' : 'Must match the email the user signs in with.' }}</mat-hint>
        </mat-form-field>

        <mat-form-field appearance="outline" class="full-width">
          <mat-label>First Name</mat-label>
          <input matInput formControlName="firstName" placeholder="Enter first name">
          <mat-icon matSuffix>person</mat-icon>
          @if (form.get('firstName')?.hasError('required')) {
            <mat-error>First name is required</mat-error>
          }
        </mat-form-field>

        <mat-form-field appearance="outline" class="full-width">
          <mat-label>Last Name</mat-label>
          <input matInput formControlName="lastName" placeholder="Enter last name">
          <mat-icon matSuffix>person</mat-icon>
          @if (form.get('lastName')?.hasError('required')) {
            <mat-error>Last name is required</mat-error>
          }
        </mat-form-field>

        <div class="activate-row">
          <mat-slide-toggle formControlName="activated">Activate user</mat-slide-toggle>
          <p class="activate-hint">
            When on, this person is allowed into the app as soon as they sign in{{ sso ? ' via SSO' : '' }}.
            Leave off to require an admin to activate them later.
          </p>
        </div>

        @if (errorMessage()) {
          <p class="error-banner">{{ errorMessage() }}</p>
        }
      </form>
    </mat-dialog-content>

    <mat-dialog-actions align="end">
      <button mat-stroked-button (click)="onCancel()" [disabled]="saving()">
        <mat-icon>close</mat-icon>
        Cancel
      </button>
      <button mat-raised-button color="primary"
              (click)="onSave()"
              [disabled]="form.invalid || isDuplicate() || saving()">
        @if (saving()) {
          <mat-spinner diameter="18"></mat-spinner>
        } @else {
          <mat-icon>person_add</mat-icon>
        }
        {{ saving() ? 'Adding...' : 'Add User' }}
      </button>
    </mat-dialog-actions>
  `,
  styles: [`
    h2[mat-dialog-title] {
      display: flex;
      align-items: center;
      gap: 8px;
      margin: 0;
      padding: 16px 24px;
      border-bottom: 1px solid #e0e0e0;
    }

    mat-dialog-content {
      padding: 24px;
      min-width: 380px;
    }

    .full-width {
      width: 100%;
      margin-bottom: 16px;
    }

    .activate-row {
      margin: 4px 0 8px;
    }

    .activate-hint {
      margin: 8px 0 0;
      font-size: 12px;
      color: rgba(0, 0, 0, 0.6);
    }

    .error-banner {
      margin: 12px 0 0;
      color: #b00020;
      font-size: 13px;
    }

    mat-dialog-actions {
      padding: 16px 24px;
      border-top: 1px solid #e0e0e0;
    }

    mat-dialog-actions button {
      margin-left: 8px;
    }

    mat-dialog-actions button mat-icon,
    mat-dialog-actions button mat-spinner {
      margin-right: 4px;
    }
  `]
})
export class AddUserDialogComponent implements OnInit {
  /** Example address in the org's own domain (env `userEmailExample`); generic when unset. */
  readonly emailExample: string = (environment as any).userEmailExample || 'name@example.com';
  /** Only SSO orgs should mention SSO in the hints. */
  readonly sso = !!environment.sso;

  private dialogRef = inject(MatDialogRef<AddUserDialogComponent>);
  private fb = inject(FormBuilder);
  private usersService = inject(UsersService);
  private permissionsClient = inject(PermissionsClientService);

  form: FormGroup;
  saving = signal(false);
  errorMessage = signal('');

  // Normalized emails already in the roster, for instant duplicate feedback.
  private existingEmails = signal<Set<string>>(new Set());

  // True when the typed email already exists in the roster (client-side guard;
  // the backend 409 is the authoritative check).
  readonly isDuplicate = computed(() => {
    const email = normalizeEmail(this.form?.get('email')?.value);
    return !!email && this.existingEmails().has(email);
  });

  constructor() {
    this.form = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
      firstName: ['', Validators.required],
      lastName: ['', Validators.required],
      activated: [false],
    });
  }

  async ngOnInit() {
    try {
      const users = await this.usersService.getAllUsersOnce();
      this.existingEmails.set(new Set((users || [])
        .map(u => normalizeEmail(u.email))
        .filter(Boolean)));
    } catch {
      // Roster unavailable — fall back to the backend 409 as the duplicate guard.
    }
  }

  async onSave() {
    if (this.form.invalid || this.isDuplicate() || this.saving()) return;

    this.saving.set(true);
    this.errorMessage.set('');
    const { email, firstName, lastName, activated } = this.form.value;

    try {
      await this.permissionsClient.createUser({ email, firstName, lastName, activated: !!activated });
      this.dialogRef.close(true);
    } catch (err: any) {
      this.saving.set(false);
      if (err?.status === 409) {
        this.errorMessage.set('This user already exists — edit them from the list or Permissions instead.');
      } else if (err?.status === 403) {
        this.errorMessage.set('You do not have permission to add users.');
      } else if (err?.status === 404) {
        this.errorMessage.set('Adding users is not available yet for this org.');
      } else {
        this.errorMessage.set('Could not add the user. Please try again.');
      }
    }
  }

  onCancel() {
    this.dialogRef.close(false);
  }
}
